import { ZIO } from '@mxt/zio'
import {
  AppContext,
  BillingChange,
  BillingChangeService,
  formatNumberWithComma,
  Frequency,
  FrequencyMapping,
  PulseOpsApi,
  Select,
  Title,
  TransactionType
} from '@pulseops/common'
import { pipe } from 'fp-ts/lib/function'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { SafeAreaView, StyleSheet, Text, View } from 'react-native'
import { RequestAuthenticateData } from '../../request-authen'
import { PolicyServiceProps } from '../policy-service-props'
import * as O from 'fp-ts/lib/Option'

type Option = {
  value: Frequency
  label: string
}

export interface BillingChangeFormData {
  newBillingFrequency: Option | null
}

export const BillingChangeScreen: React.FC<PolicyServiceProps<BillingChange.SubmitData>> = ({
  initSubmission,
  isConfirmed,
  policyNumber
}) => {
  const mapFrequency = FrequencyMapping.mapFrequency
  const policyNum = policyNumber ?? ''
  const { t } = useTranslation()
  const { showGlobalLoading, showToast } = React.useContext(AppContext.AppContextInstance)

  const [isNextInstallValid, setIsNextInstallValid] = React.useState<boolean>(false)
  const [isError, setIsError] = React.useState<boolean>(false)

  const [detailData, setDetailData] = React.useState<BillingChange.Detail>({
    curBillingFrequency: '',
    curInstallmentPremium: '',
    newBillingFrequency: null,
    newInstallmentPremium: '',
    suspenseAmount: '',
    advancePremium: '',
    effectiveDateNewFrequency: '',
    requirePayinAmount: '',
    billToDate: '',
    paidToDate: '',
    curRenewalPremium: '',
    validNewInstallmentPremium: false,
    validProduct: false
  })
  const billingChangeForm = useForm<BillingChangeFormData>()
  const { control, setValue, watch } = billingChangeForm

  React.useEffect(() => {
    loadingDetailData()
  }, [])

  const MapFrequencies = FrequencyMapping.FrequencyOption.filter(
    (freq) => freq.value !== detailData.curBillingFrequency
  )

  initSubmission({
    validate: async () => {
      loadingDetailWithNextInstall(watch('newBillingFrequency')?.value || '')
      if ((await billingChangeForm.trigger()) && isNextInstallValid && !isError) {
        const newFrequencyItem = watch('newBillingFrequency')?.value
        return {
          url: (policyNumber: string) => `wf-api/policy/${policyNumber}/billing-change`,
          body: {
            curRenewalPremium: detailData.curRenewalPremium,
            suspenseAmount: detailData.suspenseAmount,
            advancePremium: detailData.advancePremium,
            billToDate: detailData.billToDate,
            paidToDate: detailData.paidToDate,
            newBillingFrequency: newFrequencyItem || null,
            curBillingFrequency: detailData.curBillingFrequency,
            curInstallmentPremium: detailData.curInstallmentPremium,
            newInstallmentPremium: detailData.newInstallmentPremium,
            effectiveDateNewFrequency: detailData.effectiveDateNewFrequency,
            requirePayinAmount: detailData.requirePayinAmount
          },
          transactionName: RequestAuthenticateData.TransactionLabelShort(TransactionType.BILLING_CHANGE),
          collerationId: policyNum,
          transaction: TransactionType.BILLING_CHANGE
        }
      } else {
        return false
      }
    },
    clear: () => {
      loadingDetailData()
    }
  })

  const loadingDetailData = () => {
    showGlobalLoading(true)
    pipe(
      ZIO.zipPar(
        BillingChangeService.getDetail(policyNum),
        PulseOpsApi.getAccountBalance({
          policyNum: policyNum,
          function: 'TOTAL',
          accountGroups: [{ sacsCode: 'LP', sacsType: 'U' }]
        })
      ),
      ZIO.map(([responseData, accountBalanceLP_U]) => {
        if (!responseData.validNewInstallmentPremium) {
          showToast(t('message:MS050220'), 'error')
          setIsError(true)
        } else {
          const receiptILP = pipe(
            O.fromNullable(accountBalanceLP_U.body.accountGroups),
            O.fold(
              () => 0,
              (balances) => balances.reduce((total, e) => {
                return total + (e.sacsCode === 'LP' && e.sacsType === 'U' ? e.amount : 0)
              }, 0)
            )
          )
          setDetailData({
            ...responseData,
            requirePayinAmount: (
              Number(responseData.newInstallmentPremium) -
              (parseInt(responseData.suspenseAmount)
                + parseInt(responseData.advancePremium)
                + receiptILP
              )
            ).toString()
          })
          const newFrequency = detailData?.newBillingFrequency
            ? FrequencyMapping.FrequencyOption.filter((item) => item.value === detailData.newBillingFrequency).map(
              (item: Option) => ({
                label: t(`${item.label}`),
                value: item.value
              })
            )[0]
            : null
          setValue('newBillingFrequency', newFrequency)
        }
        return responseData
      }),
      ZIO.tap((_) => {
        showGlobalLoading(false)
        return ZIO.unit
      }),
      ZIO.tapError((_) => {
        showGlobalLoading(false)
        return ZIO.unit
      }),
      ZIO.unsafeRun({})
    )
  }

  const loadingDetailWithNextInstall = (frequency: string) => {
    showGlobalLoading(true)
    pipe(
      ZIO.zipPar(
        BillingChangeService.getDetailWithNextInstall(policyNum, frequency),
        PulseOpsApi.getAccountBalance({
          policyNum: policyNum,
          function: 'TOTAL',
          accountGroups: [{ sacsCode: 'LP', sacsType: 'U' }]
        })
      ),
      ZIO.fold(
        (err) => {
          const source: any = err.source
          const code = source['response']?.data?.responseStatus?.errors[0]?.code
          const message = source['response']?.data?.responseStatus?.message

          if (code && message) {
            if (code === 'RL11') {
              showToast(t('message:MS050220'), 'error')
            } else if (code === 'F009') {
              const currFrequency = mapFrequency.get(frequency)
              const codeAvailable = MapFrequencies.reduce((acc, curr) => {
                return curr.value === frequency ? acc : acc.length ? acc + ' hoặc ' + curr.label : curr.label
              }, '')
              showToast(t('message:MS050215', { currFrequency, codeAvailable }), 'error')
            } else {
              showToast(message, 'error')
            }
            setIsError(true)
          }
          setIsNextInstallValid(false)
          const reqPayin = -(Number(detailData.suspenseAmount) + Number(detailData.advancePremium))
          if (!detailData.validNewInstallmentPremium) {
            showToast(t('message:MS050220'), 'error')
            setIsError(true)
          } else {
            setDetailData({
              ...detailData,
              newInstallmentPremium: '0',
              requirePayinAmount: reqPayin > 0 ? reqPayin.toString() : '0'
            })
          }
        },
        ([detail, accountBalanceLP_U]) => {
          if (!detail.validProduct && frequency === Frequency.Monthly) {
            setIsError(true)
            showToast(t('message:MS050217'), 'error')
          }
          // else if (detail.curInstallmentPremium === detail.advancePremium) {
          //   setIsError(true)
          //   showToast(t('message:MS050218'), 'error')
          // }
          else if (detail.newInstallmentPremium === '0') {
            setIsError(true)
            showToast(t('message:MS050219'), 'error')
          } else {
            setIsError(false)
            setIsNextInstallValid(true)
            if (!detail.validNewInstallmentPremium) {
              showToast(t('message:MS050220'), 'error')
              setIsError(true)
            } else {
              const receiptILP = pipe(
                O.fromNullable(accountBalanceLP_U.body.accountGroups),
                O.fold(
                  () => 0,
                  (balances) => balances.reduce((total, e) => {
                    return total + (e.sacsCode === 'LP' && e.sacsType === 'U' ? e.amount : 0)
                  }, 0)
                )
              )
              setDetailData({
                ...detail,
                requirePayinAmount: (
                  Number(detail.newInstallmentPremium) -
                  (parseInt(detail.suspenseAmount)
                    + parseInt(detail.advancePremium)
                    + receiptILP
                  )
                ).toString()
              })
            }
          }
          return detail
        }
      ),
      ZIO.tap((_) => {
        showGlobalLoading(false)
        return ZIO.unit
      }),
      ZIO.tapError((_) => {
        showGlobalLoading(false)
        return ZIO.unit
      }),
      ZIO.unsafeRun({})
    )
  }

  const onChangeFrequency = (frequency: Frequency) => {
    loadingDetailWithNextInstall(frequency)
  }

  return (
    <SafeAreaView style={{ flex: 1, marginTop: 15 }}>
      <Title title={t('TransactionType:BILLING_CHANGE')} />
      <View style={billingStyles.sectionContent}>
        <View style={billingStyles.sectionRow}>
          <View style={billingStyles.col_4}>
            <Text style={billingStyles.field_title}>{t('requestInfo:CurrentBillingFrequency')}</Text>
            <Text style={billingStyles.field_description}>
              {detailData?.curBillingFrequency
                ? t(mapFrequency.get(detailData.curBillingFrequency as Frequency) ?? '') ?? ''
                : ''}
            </Text>
          </View>
          <View style={billingStyles.col_4}>
            <Text style={billingStyles.field_title}>{t('requestInfo:CurentInstallmentPremium')}</Text>
            <Text style={billingStyles.field_description}>
              {detailData.curInstallmentPremium ? formatNumberWithComma(detailData.curInstallmentPremium) : '0'} VND
            </Text>
          </View>
          <View style={billingStyles.col_4}>
            <Text style={billingStyles.field_title}>{t('requestInfo:EffectiveDateOfNewFrequency')}</Text>
            <Text style={billingStyles.field_description}>{detailData?.effectiveDateNewFrequency || ''}</Text>
          </View>
        </View>

        <View style={[billingStyles.sectionRow, billingStyles.secondLine]}>
          <View style={billingStyles.col_4}>
            <Controller
              name="newBillingFrequency"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: `${t('message:MS020009', { field: t('requestInfo:NewBillingFrequency') })}`
                }
              }}
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <Select
                  onChange={(val) => {
                    const selectValue = val?.value as Frequency
                    onChange(val)
                    onChangeFrequency(selectValue)
                  }}
                  onBlur={onBlur}
                  value={value}
                  label={t('requestInfo:NewBillingFrequency')}
                  disabled={isConfirmed}
                  showUnderline={!isConfirmed}
                  errorMessage={error?.message}
                  options={MapFrequencies.map((item) => ({
                    label: t(`${item.label}`),
                    value: item.value
                  }))}
                ></Select>
              )}
            />
          </View>
          <View style={billingStyles.col_4}>
            <Text style={billingStyles.field_title}>{t('requestInfo:NewInstallmentPremium')}</Text>
            <Text style={billingStyles.field_description}>
              {detailData.newInstallmentPremium ? formatNumberWithComma(detailData.newInstallmentPremium) : '0'} VND
            </Text>
          </View>
          <View style={billingStyles.col_4}>
            <Text style={billingStyles.field_title}>{t('requestInfo:RequirePayinAmount')}</Text>
            <Text style={[billingStyles.field_description, billingStyles.red_line]}>
              {detailData.requirePayinAmount && Number(detailData.requirePayinAmount) > 0
                ? formatNumberWithComma(detailData.requirePayinAmount)
                : '0'}{' '}
              VND
            </Text>
          </View>
        </View>
      </View>
    </SafeAreaView>
  )
}
const billingStyles = StyleSheet.create({
  headerField: {
    fontSize: 15,
    fontWeight: '700'
  },
  sectionContent: {
    padding: 20,
    backgroundColor: '#fafafa',
    border: '1px solid #d3dce6',
    boxSizing: 'border-box',
    borderRadius: 8
  },
  sectionRow: {
    flex: 1,
    flexDirection: 'row',
    marginRight: -15,
    marginLeft: -15
  },
  secondLine: {
    marginTop: 20
  },
  col_4: {
    width: '100%',
    maxWidth: '33.3333333333%',
    paddingRight: 15,
    paddingLeft: 15
  },
  field_title: {
    color: '#70777E',
    fontSize: 15,
    fontWeight: 'bold',
    lineHeight: 20,
    marginBottom: 8
  },
  field_description: {
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: 15,
    lineHeight: 22,
    color: '#000000'
  },
  red_line: {
    fontWeight: 'bold',
    color: '#ed1c2e'
  }
})
